/**
 * VERA-1095: the ONE password policy shared by every surface that collects or
 * validates a password client-side — the self-serve signup password field
 * (`SignUpPage`), password-setup/password-reset (`PasswordSetup`, both its
 * "Create password" and "New password" modes), and the strength meter shown
 * on `PasswordSetup`.
 *
 * Why this policy, and why it's safe against the backend without a backend
 * change: `apps/api`'s own password DTOs (`signup.public.controller.ts`'s
 * `CreateSignupCompanyBodyDto`, `change-user-password.request.dto.ts`) only
 * enforce `@MinLength(8)` — they deliberately do NOT duplicate a composition
 * rule, and instead defer the rest of strength enforcement to WorkOS (which
 * runs its own SCORED strength check — length + common/breached-password
 * detection — not a fixed regex; see the comment on
 * `WorkOSAuthService.setupPassword`, VERA-842). Since every password this
 * policy accepts is at least 8 characters, it is a strict SUPERSET of the
 * backend's own rule, so nothing accepted here can ever be rejected by the
 * backend's length check — no backend change was needed to bring the two
 * into agreement. It cannot, however, GUARANTEE WorkOS's acceptance: WorkOS's
 * scorer also rejects common/breached passwords irrespective of character-
 * class composition, which is why `mapWorkosPasswordError` below still exists
 * as a residual-rejection fallback after this policy's own checks pass.
 *
 * Previously three different rules covered the same credential (VERA-1095
 * bug report): the self-serve signup form checked `minLength(8)` only, this
 * page (`PasswordSetup`) checked `length >= 8` only, and a stricter
 * composition rule (`passwordRex`, in `constants/regex.ts`) existed but was
 * wired into NEITHER — only into an unused, never-imported `FieldPassword`
 * component (itself since deleted). A password that passed both frontend checks could still be
 * rejected by WorkOS's own strength check with no explanation of why. This
 * module is now the single place all of the above read from.
 *
 * `passwordRex` itself has since been DELETED (VERA-1095): it was a second
 * implementation of this same rule and the two had drifted — see the note left
 * in `constants/regex.ts`, and `assertPolicyIntent` below.
 */

export const PASSWORD_MIN_LENGTH = 8

/**
 * One-line prose version of PASSWORD_REQUIREMENTS, for surfaces that show a
 * single validation message rather than the full checklist (e.g. `SignUpPage`'s
 * react-hook-form `validate` message). Kept as prose derived from the same
 * requirements array so it can never list a rule the checklist doesn't also
 * enforce.
 *
 * It is also re-exported as `passwordValidationText` from `constants/messages.ts`.
 * That alias's only consumer was the dead `FieldPassword` field, deleted in
 * VERA-1095; the alias is left in place because the name is the one the older
 * field components use and re-pointing them is not this change's business.
 */
export const PASSWORD_POLICY_HELP_TEXT =
  `At least ${PASSWORD_MIN_LENGTH} characters, including a lowercase letter, an uppercase letter, a number, and a special character.`

export interface PasswordRequirement {
  id: string
  label: string
  test: (password: string) => boolean
}

// Individual rule breakdown, and the gate itself. Kept as separate, simple
// per-class regexes rather than one aggregate expression for two reasons: the
// checklist can tell the user WHICH rule is unmet rather than just that
// validation failed, and — the load-bearing one — every rule here is a POSITIVE
// assertion ("contains a lowercase letter"). None of them is an allowlist over
// the whole string, so no character is rejected for merely not being
// enumerated. That is exactly the property the deleted `passwordRex` lacked.
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'length',
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (password) => password.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    test: (password) => /[a-z]/.test(password),
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'number',
    label: 'One number',
    test: (password) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    // A POSITIVE test — "does the password contain one of these" — never an
    // allowlist. A character absent from this class (a space, `é`, a curly
    // quote) does not make the password invalid; it just doesn't count as the
    // special character. Do not invert this into a whole-string match.
    label: 'One special character',
    test: (password) => /[!@#$%^&*()|_+\-={}[\]\\/<>?,.:"';~`]/.test(password),
  },
]

/** The single source of truth for "is this password acceptable". */
export const passwordMeetsPolicy = (password: string): boolean =>
  PASSWORD_REQUIREMENTS.every((requirement) => requirement.test(password))

/**
 * Dev-time self-check on the policy's INTENT.
 *
 * What this used to guard, and why it no longer can: it compared
 * `passwordMeetsPolicy` against `passwordRex`, a second implementation of the
 * same rule, and reported when the two disagreed. It found a real bug —
 * `"Aa1! aaaa"` (a space) and `"Aa1!aaaé"` (an accented letter) satisfied every
 * requirement in the checklist while failing the regex, so any surface gated on
 * the regex showed a user five green ticks and then refused to submit, with a
 * message reciting the five rules they had just satisfied.
 *
 * `passwordRex` is now deleted (see `constants/regex.ts`), so there is nothing
 * left to disagree with and that particular drift is structurally impossible
 * rather than merely absent today. But "the two agree" was never the property
 * worth protecting — it is satisfiable by making BOTH sides wrong. The property
 * that matters is that the policy accepts what it intends to accept, so that is
 * what is asserted here directly: a table of probes with expected verdicts.
 *
 * Concretely this now guards against:
 *   - Someone reintroducing a whole-string allowlist (the exact defect above) —
 *     the space / non-ASCII / typographic probes fail the moment one appears.
 *   - Someone relaxing the policy so a trivially weak password passes.
 *   - `PASSWORD_MIN_LENGTH` and the `length` requirement drifting apart.
 *
 * On the two "reject" probes being a real bar and not decoration: `passwordRex`
 * would have passed this table's *reject* rows too. It is the *accept* rows it
 * failed, which is the asymmetry to keep in mind when adding probes — a
 * narrowing bug hides in what a rule refuses, not in what it permits.
 */
if (import.meta.env.DEV) {
  // NIST SP 800-63B §5.1.1.2 is explicit that all printing ASCII, the SPACE
  // character, and Unicode characters SHOULD be accepted in a memorized secret.
  // Rejecting them narrows the space a user can draw from, which weakens the
  // policy — it is not a security control, and it silently breaks anyone using
  // a passphrase or a non-English keyboard layout.
  const INTENT_PROBES: { password: string; expected: boolean; why: string }[] = [
    { password: 'Aa1!aaaa', expected: true, why: 'happy path — every requirement met, plain ASCII' },
    { password: 'Aa1! aaaa', expected: true, why: 'a SPACE is a legitimate password character (NIST SP 800-63B)' },
    { password: 'Aa1!aaaé', expected: true, why: 'a non-ASCII letter is legitimate — non-English keyboards' },
    { password: 'Aa1!aaa“x', expected: true, why: 'a typographic quote is legitimate — iOS/macOS smart quotes' },
    { password: `Aa1!${'a'.repeat(100)}`, expected: true, why: 'no maximum length — long passphrases must be accepted' },
    { password: 'abc', expected: false, why: 'too short, and missing every character class' },
    { password: 'a'.repeat(20), expected: false, why: 'long but single-class — length alone is not sufficient' },
    { password: `Aa1${'a'.repeat(10)}`, expected: false, why: 'no special character' },
    { password: 'Aa1!aaa', expected: false, why: `${PASSWORD_MIN_LENGTH - 1} chars — one under the minimum` },
  ]

  const violations = INTENT_PROBES.filter(({ password, expected }) => passwordMeetsPolicy(password) !== expected)

  if (violations.length > 0) {
    // eslint-disable-next-line no-console
    console.error(
      '[passwordPolicy] PASSWORD_REQUIREMENTS no longer matches the policy\'s stated intent:\n'
        + violations
          .map(
            ({ password, expected, why }) =>
              `  ${JSON.stringify(password)} — should be ${expected ? 'ACCEPTED' : 'REJECTED'} (${why}),`
              + ` but passwordMeetsPolicy returned ${!expected}.`,
          )
          .join('\n')
        + '\nFix the requirement you just changed, or — if the intent itself has genuinely changed —'
        + ' update this probe table and say why in the commit.',
    )
  }
}

export type PasswordStrengthLabel = 'Weak' | 'Fair' | 'Good' | 'Strong'

export interface PasswordStrength {
  metCount: number
  total: number
  label: PasswordStrengthLabel
}

/**
 * Rule-based strength score against OUR OWN policy above (no zxcvbn / third-
 * party dependency, per VERA-1095 scope) — the bar and the submit gate can
 * never disagree because both read `PASSWORD_REQUIREMENTS`. "Strong" is only
 * ever reported once every requirement is met, i.e. exactly when
 * `passwordMeetsPolicy` would also allow submit.
 */
export const getPasswordStrength = (password: string): PasswordStrength => {
  const total = PASSWORD_REQUIREMENTS.length
  const metCount = password
    ? PASSWORD_REQUIREMENTS.filter((requirement) => requirement.test(password)).length
    : 0

  const label: PasswordStrengthLabel =
    metCount <= 1 ? 'Weak' : metCount <= 3 ? 'Fair' : metCount === 4 ? 'Good' : 'Strong'

  // NO colour is returned. The bar draws the shared house gradient
  // (`PROGRESS_BAR_GRADIENT`) at every fill level — see PasswordStrengthMeter.
  //
  // It used to return a colour: a white alpha while filling, Vera purple once
  // every requirement was met. That broke in LIGHT mode, and unavoidably so —
  // `rgba(255,255,255,0.35)` on a light track is invisible, which is exactly
  // what it looked like. The old note here even said why, and treated it as
  // acceptable: "both surfaces that render this meter are the near-black auth
  // pages". That stopped being true when the auth pages became theme-aware, and
  // a value chosen because one mode was the only mode is a latent bug the moment
  // a second mode exists.
  //
  // The two rules the ORIGINAL decision was protecting both survive the change,
  // which is why the gradient is safe rather than a regression:
  //
  //   - No red -> amber -> green RAMP. Red is the error colour, and on the
  //     signup form an error renders directly above this bar; painting ordinary
  //     progress in it teaches people to discount red for when it means
  //     something. The gradient is a single brand hue, not a semantic scale, so
  //     it never claims "bad".
  //   - Colour is not carrying the information. The bar's LENGTH encodes how far
  //     along you are and the checklist below names each unmet rule with a
  //     check/✕ glyph. That was the argument against the ramp — colour was a
  //     third encoding of a fact already stated twice — and it applies just as
  //     well against a neutral->purple flip. Nothing is lost by dropping it, and
  //     red-green colour blindness (~8% of men) is unaffected either way.
  //
  // What IS given up: the completion moment no longer has its own colour change.
  // The fifth checklist row flipping ✕ -> ✓ and the bar reaching full width both
  // land at that instant, so the moment is still marked twice.
  return { metCount, total, label }
}

/**
 * VERA-1095: maps WorkOS's raw strength-rejection string to something
 * actionable instead of passing it through verbatim (it was previously
 * relayed as-is — see WorkOSAuthService.setupPassword's VERA-842 comment).
 * By the time this fires, the password has already satisfied every rule in
 * PASSWORD_REQUIREMENTS (submit is blocked before that), so the likely
 * residual cause is WorkOS's own common/breached-password check, which our
 * client-side regex policy cannot detect.
 */
export const mapWorkosPasswordError = (message: string | null | undefined): string => {
  if (!message) return 'That password was rejected. Please try a different one.'
  if (/strength/i.test(message)) {
    return "That password is too common or has appeared in a data breach, so it can't be used. Please choose a different one."
  }
  return message
}
